/** 記事のRepository。UIはこの層だけを触り、OneDriveを直接参照しない（仕様書 §3）。 */

import { getDb, type EntryRecord } from './schema';
import type { Entry, MonthKey } from '../domain/types';
import { SCHEMA_VERSION } from '../domain/types';
import { getDeviceId, newId } from '../domain/ids';
import { dateKeyOfIsoLocal, monthKeyOfIsoLocal, nowIsoUtc, toIsoLocal } from '../lib/datetime';
import { buildSearchText } from '../lib/text';
import { enqueue } from './outbox';
import { bumpData } from './events';

function toRecord(e: Entry, dirty: boolean, etag: string | null): EntryRecord {
  return {
    ...e,
    monthKey: monthKeyOfIsoLocal(e.date),
    dateKey: dateKeyOfIsoLocal(e.date),
    searchText: buildSearchText(e.title, e.body),
    _dirty: dirty,
    _etag: etag,
  };
}

export function createDraft(date = new Date(), categoryId: string | null = null): Entry {
  const now = nowIsoUtc();
  return {
    schemaVersion: SCHEMA_VERSION,
    id: newId(),
    date: toIsoLocal(date),
    createdAt: now,
    updatedAt: now,
    rev: 0,
    deviceId: getDeviceId(),
    categoryId,
    title: '',
    body: '',
    location: null,
    media: [],
  };
}

export async function getEntry(id: string): Promise<EntryRecord | undefined> {
  const db = await getDb();
  return db.get('entries', id);
}

/** 保存。ローカルへ確定させてから outbox に積む。ネットワークは待たない（§12.7）。 */
export async function saveEntry(entry: Entry): Promise<EntryRecord> {
  const db = await getDb();
  const existing = await db.get('entries', entry.id);
  const prevMonth = existing?.monthKey;

  const next: Entry = {
    ...entry,
    schemaVersion: SCHEMA_VERSION,
    deviceId: getDeviceId(),
    updatedAt: nowIsoUtc(),
    rev: (existing?.rev ?? 0) + 1,
  };
  const rec = toRecord(next, true, existing?._etag ?? null);
  await db.put('entries', rec);

  await enqueue({
    type: 'entry.upsert',
    targetKey: `entry:${rec.id}`,
    entryId: rec.id,
    month: rec.monthKey,
    // 月をまたいだ場合はファイル移動が必要になる（§7.6）
    prevMonth: prevMonth && prevMonth !== rec.monthKey ? prevMonth : undefined,
  });
  bumpData();
  return rec;
}

/** 同期処理からの適用。updatedAt を書き換えず、rev も増やさない。 */
export async function putFromRemote(entry: Entry, etag: string | null): Promise<void> {
  const db = await getDb();
  await db.put('entries', toRecord(entry, false, etag));
  bumpData();
}

export async function deleteEntry(id: string): Promise<void> {
  const db = await getDb();
  const rec = await db.get('entries', id);
  if (!rec) return;

  await db.put('tombstones', {
    entryId: id,
    entryMonth: rec.monthKey,
    deletedAt: nowIsoUtc(),
    deviceId: getDeviceId(),
  });
  await db.delete('entries', id);

  const mediaKeys = await db.getAllKeysFromIndex('media', 'by_entry', id);
  await Promise.all(mediaKeys.map((k) => db.delete('media', k)));
  const strokeKeys = await db.getAllKeysFromIndex('strokes', 'by_entry', id);
  await Promise.all(strokeKeys.map((k) => db.delete('strokes', k)));

  await enqueue({
    type: 'entry.delete',
    targetKey: `entry:${id}`,
    entryId: id,
    month: rec.monthKey,
  });
  bumpData();
}

/** dateKey が示す1日分。date 昇順。 */
export async function listByDate(dateKey: string): Promise<EntryRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(
    'entries',
    'by_date',
    IDBKeyRange.bound(dateKey, dateKey + '￿'),
  );
  return all.sort(compareByDate);
}

/** 期間内（両端含む）。date 昇順。 */
export async function listByRange(fromDateKey: string, toDateKey: string): Promise<EntryRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(
    'entries',
    'by_date',
    IDBKeyRange.bound(fromDateKey, toDateKey + '￿'),
  );
  return all.sort(compareByDate);
}

export async function listByMonth(monthKey: MonthKey): Promise<EntryRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('entries', 'by_month', monthKey);
  return all.sort(compareByDate);
}

export async function listAll(): Promise<EntryRecord[]> {
  const db = await getDb();
  const all = await db.getAll('entries');
  return all.sort(compareByDate);
}

export async function countAll(): Promise<number> {
  const db = await getDb();
  return db.count('entries');
}

export function compareByDate(a: EntryRecord, b: EntryRecord): number {
  if (a.date === b.date) return a.id < b.id ? -1 : 1;
  return a.date < b.date ? -1 : 1;
}
